import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, Check, Info } from "lucide-react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import Seo from "@/components/Seo";
import { CONSULTING_PAGES } from "@/data/consulting";

function BlockContent({ block }) {
  return (
    <>
      {block.paragraphs?.map((p, j) => (
        <p key={j} className="mt-5 text-base leading-relaxed text-ink-muted">
          {p}
        </p>
      ))}
      {block.list && (
        <ul className="mt-6 space-y-3">
          {block.list.map((item, j) => (
            <li
              key={j}
              className="flex items-start gap-3 text-base leading-relaxed text-ink-muted"
            >
              <Check className="mt-1 h-4 w-4 shrink-0 text-clay" />
              {item}
            </li>
          ))}
        </ul>
      )}
      {block.linkList && (
        <ul className="mt-8 border-t border-line">
          {block.linkList.map((l) => (
            <li key={l.href} className="border-b border-line">
              <Link
                to={l.href}
                data-testid={`consulting-link-${l.href.replace(/\//g, "-")}`}
                className="group flex items-center justify-between gap-6 py-5"
              >
                <span>
                  <span className="font-serif text-lg tracking-tight text-ink transition-colors group-hover:text-clay sm:text-xl">
                    {l.label}
                  </span>
                  <span className="mt-1 block text-sm leading-relaxed text-ink-muted">
                    {l.note}
                  </span>
                </span>
                <ArrowUpRight className="h-5 w-5 shrink-0 text-clay transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

export default function ConsultingPage({ page }) {
  const others = CONSULTING_PAGES.filter((p) => p.slug !== page.slug);

  return (
    <>
      <Seo
        title={page.title}
        description={page.description}
        keywords={page.keywords}
        jsonLd={[
          {
            "@type": "Service",
            name: page.title,
            description: page.description,
            inLanguage: "de",
            provider: {
              "@type": "ProfessionalService",
              name: "Florida Business & Relocation",
            },
            areaServed: { "@type": "State", name: "Florida" },
          },
          {
            "@type": "FAQPage",
            mainEntity: page.faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Startseite", item: "https://auswandernflorida.com/" },
              { "@type": "ListItem", position: 2, name: "Leistungen", item: "https://auswandernflorida.com/leistungen" },
              { "@type": "ListItem", position: 3, name: page.title },
            ],
          },
        ]}
      />
      <Header />
      <main>
        <article
          data-testid="consulting-page"
          className="bg-sand pb-24 pt-36 lg:pt-44"
        >
          <div className="mx-auto max-w-3xl px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Link
                to="/leistungen"
                data-testid="consulting-back-link"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink-muted transition-colors hover:text-clay"
              >
                <ArrowLeft className="h-4 w-4" /> Leistungen
              </Link>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="mt-10 text-xs uppercase tracking-[0.3em] text-clay"
              data-testid="consulting-badge"
            >
              {page.badge}
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-6 font-serif text-4xl leading-tight tracking-tight text-ink sm:text-5xl"
              data-testid="consulting-title"
            >
              {page.title}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-8 border-l-2 border-clay pl-6 font-serif text-xl italic leading-relaxed text-ink-muted"
              data-testid="consulting-lead"
            >
              {page.lead}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-10"
            >
              <a
                href="/kontakt"
                data-testid="consulting-cta-top"
                className="group inline-flex items-center gap-3 bg-clay px-8 py-4 text-xs uppercase tracking-[0.2em] text-sand transition-colors hover:bg-clay-dark"
              >
                Kostenlose Chancenprüfung
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
            </motion.div>

            <div className="mt-16 space-y-16" data-testid="consulting-body">
              {page.blocks.map((block, i) => (
                <motion.section
                  key={block.heading}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6 }}
                  data-testid={`consulting-section-${i}`}
                >
                  <h2 className="font-serif text-2xl tracking-tight text-ink sm:text-3xl">
                    {block.heading}
                  </h2>
                  <BlockContent block={block} />
                  {block.sub?.map((s, k) => (
                    <div key={k} className="mt-10">
                      <h3 className="font-serif text-xl tracking-tight text-ink sm:text-2xl">
                        {s.heading}
                      </h3>
                      <BlockContent block={s} />
                    </div>
                  ))}
                </motion.section>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6 }}
              className="mt-16 border-l-2 border-clay bg-sand-deep p-8 sm:p-10"
              data-testid="consulting-note-box"
            >
              <p className="flex items-center gap-3 font-serif text-xl italic tracking-tight text-ink sm:text-2xl">
                <Info className="h-5 w-5 shrink-0 text-clay" />
                {page.myth.title}
              </p>
              <p className="mt-4 text-sm leading-relaxed text-ink-muted md:text-base">
                {page.myth.text}
              </p>
            </motion.div>

            <motion.section
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6 }}
              className="mt-20"
              data-testid="consulting-faq"
            >
              <h2 className="font-serif text-2xl tracking-tight text-ink sm:text-3xl">
                {page.faqTitle}
              </h2>
              <div className="mt-8 space-y-10">
                {page.faq.map((f, i) => (
                  <div key={i} data-testid={`consulting-faq-item-${i}`}>
                    <h3 className="font-serif text-lg tracking-tight text-ink sm:text-xl">
                      {f.q}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-ink-muted md:text-base">
                      {f.a}
                    </p>
                  </div>
                ))}
              </div>
            </motion.section>

            <div
              className="mt-16 border border-line bg-sand-deep p-10"
              data-testid="consulting-cta-box"
            >
              <p className="font-serif text-2xl italic leading-snug tracking-tight text-ink">
                Wie wir Sie begleiten
              </p>
              <p className="mt-4 text-sm leading-relaxed text-ink-muted md:text-base">
                {page.closing}
              </p>
              <a
                href="/kontakt"
                data-testid="consulting-cta-button"
                className="group mt-8 inline-flex items-center gap-3 bg-clay px-8 py-4 text-xs uppercase tracking-[0.2em] text-sand transition-colors hover:bg-clay-dark"
              >
                Kostenloses Erstgespräch buchen
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
              <p className="mt-6 text-xs leading-relaxed text-ink-muted/70">
                Komplettservice von A bis Z: Wir betreuen Ihr gesamtes Vorhaben
                persönlich und auf Deutsch – von der ersten Strategie bis zur
                fertigen Antragstellung.
              </p>
            </div>

            <div className="mt-24 border-t border-line pt-12">
              <p className="text-xs uppercase tracking-[0.3em] text-clay">
                Weitere Beratungsthemen
              </p>
              <ul className="mt-8 space-y-6" data-testid="consulting-more-list">
                {others.map((other) => (
                  <li key={other.slug}>
                    <Link
                      to={`/${other.slug}`}
                      data-testid={`consulting-more-${other.slug}`}
                      className="group flex items-baseline justify-between gap-6"
                    >
                      <span className="font-serif text-xl tracking-tight text-ink transition-colors group-hover:text-clay sm:text-2xl">
                        {other.title}
                      </span>
                      <ArrowRight className="h-4 w-4 shrink-0 text-clay transition-transform group-hover:translate-x-1" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}
